import React from 'react';
import { Users, BookOpen, Target, Shield, Eye, Send, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import useSuperAdminMyTeamStore from '@/stores/SuperAdminStore/SuperAdminMyTeamStore';
import './TeamDashboardTab.css';

interface TeamDashboardTabProps {
  onViewUser: (userId: string) => void;
}

const TeamDashboardTab: React.FC<TeamDashboardTabProps> = ({ onViewUser }) => {
  const { teamMembers, teamStats } = useSuperAdminMyTeamStore();

  return (
    <div className="admin-my-team-dashboard">
      <div className="admin-my-team-stats">
        <div className="admin-my-team-stat-card">
          <div className="admin-my-team-stat-content">
            <div>
              <div className="admin-my-team-stat-label">Team Members</div>
              <div className="admin-my-team-stat-value">{teamStats?.totalMembers || 0}</div>
              <div className="admin-my-team-stat-status admin-my-team-status-active">{teamStats?.activeMembers || 'Loading...'}</div>
            </div>
            <div className="admin-my-team-stat-icon admin-my-team-icon-blue">
              <Users size={28} />
            </div>
          </div>
        </div>

        <div className="admin-my-team-stat-card">
          <div className="admin-my-team-stat-content">
            <div>
              <div className="admin-my-team-stat-label">Active Learners</div>
              <div className="admin-my-team-stat-value">{teamStats?.activeLearners || 0}</div>
              <div className="admin-my-team-stat-status admin-my-team-status-learning">{teamStats?.activeLearnersStatus || 'Loading...'}</div>
            </div>
            <div className="admin-my-team-stat-icon admin-my-team-icon-orange">
              <BookOpen size={28} />
            </div>
          </div>
        </div>

        <div className="admin-my-team-stat-card">
          <div className="admin-my-team-stat-content">
            <div>
              <div className="admin-my-team-stat-label">Completion Rate</div>
              <div className="admin-my-team-stat-value">{teamStats?.completionRate || '0%'}</div>
              <div className="admin-my-team-stat-status admin-my-team-status-increase">{teamStats?.completionRateChange || 'Loading...'}</div>
            </div>
            <div className="admin-my-team-stat-icon admin-my-team-icon-green">
              <Target size={28} />
            </div>
          </div>
        </div>

        <div className="admin-my-team-stat-card">
          <div className="admin-my-team-stat-content">
            <div>
              <div className="admin-my-team-stat-label">Compliance %</div>
              <div className="admin-my-team-stat-value">{teamStats?.complianceRate || '0%'}</div>
              <div className="admin-my-team-stat-status admin-my-team-status-overdue">{teamStats?.complianceIssue || 'Loading...'}</div>
            </div>
            <div className="admin-my-team-stat-icon admin-my-team-icon-purple">
              <Shield size={28} />
            </div>
          </div>
        </div>
      </div>

      <div className="admin-my-team-section">
        <h2 className="admin-my-team-section-title">Team Members</h2>
        <div className="admin-my-team-table-container">
          <table className="admin-my-team-table">
            <thead>
              <tr>
                <th>NAME</th>
                <th>JOB TITLE</th>
                <th>PROGRESS</th>
                <th>REQUIRED TRAINING</th>
                <th>NEXT DEADLINE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {teamMembers?.map(member => (
                <tr key={member.id}>
                  <td>
                    <div className="admin-my-team-member-info">
                      <div className="admin-my-team-avatar">{member.initials}</div>
                      <div>
                        <div className="admin-my-team-member-name">{member.name}</div>
                        <div className="admin-my-team-member-email">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-my-team-job-title">{member.jobTitle}</div>
                    <div className="admin-my-team-department">{member.department}</div>
                  </td>
                  <td>
                    <div className="admin-my-team-progress-label">Completion {member.progress}%</div>
                    <div className="admin-my-team-progress-bar">
                      <div
                        className="admin-my-team-progress-fill"
                        style={{ width: `${member.progress}%` }}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="admin-my-team-training-info">
                      <span className="admin-my-team-training-badge">
                        <CheckCircle size={14} /> {member.requiredTraining}
                      </span>
                      {member.overdue > 0 && (
                        <span className="admin-my-team-overdue-badge">
                          <AlertTriangle size={14} /> {member.overdue} overdue
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="admin-my-team-deadline">
                      <Calendar size={14} /> {member.nextDeadline}
                    </div>
                  </td>
                  <td>
                    <div className="admin-my-team-actions">
                      <button
                        className="admin-my-team-action-btn"
                        onClick={() => onViewUser(member.id)}
                        title="View user details"
                      >
                        <Eye size={16} />
                      </button>
                      <button className="admin-my-team-action-btn" title="Send message">
                        <Send size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamDashboardTab;
